import * as Sentry from "@sentry/react";

// Use YOUR exact Sentry helper pattern
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'token-manager' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'token-manager' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'token-manager',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Token operation: ${operation} took ${duration}ms`,
    category: 'token-performance',
    level: 'info',
    data: { ...context, duration }
  });
};

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.abortController = null; // Like your abortControllerRef
    this.isRefreshing = false;   // Like your operationRefs pattern
  this.tokenLifetime = 60 * 60 * 1000;  // 1 hour
this.refreshBuffer = 5 * 60 * 1000;   // Refresh 5 minutes before expiry
    
    logInfo('TokenManager initialized', {
      tokenLifetime: this.tokenLifetime / 1000,
      refreshBuffer: this.refreshBuffer / 1000
    });
  }

  startProactiveRefresh() {
    logInfo('Starting proactive token refresh', {
      currentTimerExists: !!this.refreshTimer
    });
    
    try {
      this.clearRefreshTimer();
      
      const refreshTime = this.tokenLifetime - this.refreshBuffer;
      
      this.refreshTimer = setTimeout(async () => {
        await this.performProactiveRefresh();
      }, refreshTime);
      
      logInfo('Refresh timer set', {
        nextRefreshIn: refreshTime / 1000
      });
      
    } catch (error) {
      logError('Error in startProactiveRefresh', {}, error);
    }
  }

  async performProactiveRefresh() {
    // YOUR pattern: Prevent duplicates with flag check
    if (this.isRefreshing) {
      logInfo('Refresh already in progress, skipping');
      return;
    }
    
    this.isRefreshing = true;
    
    // YOUR pattern: Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    
    const startTime = performance.now();
    
    logInfo('Performing proactive token refresh', {
      timestamp: new Date().toISOString()
    });
    
    try {
      const response = await fetch('/api/auth/oauth/refresh-token', {
        method: 'POST',
        credentials: 'include',
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const endTime = performance.now();
      const duration = endTime - startTime;

      // YOUR pattern: Check response.ok first
      if (response.ok) {
        logPerformance('token_refresh', duration, {
          success: true
        });
        
        logInfo('Proactive refresh successful', {
          duration: `${duration.toFixed(2)}ms`
        });
        
        this.startProactiveRefresh();
      } else {
        // YOUR pattern: Log specific status codes
        logError('Proactive refresh failed', {
          status: response.status,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // YOUR pattern: Check AbortError first
      if (error.name === 'AbortError') {
        logInfo('Token refresh request aborted', { duration });
        return;
      }
      
      // YOUR pattern: Check network errors
      if (!error.response) {
        logError('Token refresh network error', {
          duration: `${duration.toFixed(2)}ms`
        }, error);
        return;
      }
      
      // YOUR pattern: Generic error logging
      logError('Token refresh error', {
        errorName: error.name,
        errorMessage: error.message,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
    } finally {
      // YOUR pattern: Always reset flag
      this.isRefreshing = false;
    }
  }

  stopProactiveRefresh() {
    logInfo('Stopping proactive token refresh');
    this.clearRefreshTimer();
    
    // YOUR pattern: Cancel pending request on cleanup
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      logInfo('Clearing refresh timer', {
        timerId: this.refreshTimer
      });
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  updateTokenLifetime(minutes) {
    this.tokenLifetime = minutes * 60 * 1000;
    this.refreshBuffer = 1 * 60 * 1000;
    
    logInfo('Token lifetime updated', {
      newLifetime: minutes,
      refreshBuffer: 1
    });
  }
}

export const tokenManager = new TokenManager();
export default TokenManager;